'use client';

import { useMemo } from 'react';
import { useLobbyBoards } from '@/hooks/use-lobby-boards';
import { useBoardsLand } from '@/hooks/use-board-land';
import { useTakeoverPrice } from '@/hooks/use-takeover-price';
import { formatCompactUsd } from '@/features/lobby/data/mock-prices';

/**
 * Hero stats strip — four live KPIs at the foot of the hero.
 *
 *   1. Active boards     — boards that paid out in the last 7 days.
 *   2. Total land value  — Σ each active board's land value (the sum of
 *                          the self-assessed price of its tax-paying
 *                          seats; NOT asset TVL), via `useBoardsLand`.
 *   3. Total rewards     — Σ cumulative yield + tax across all boards.
 *   4. TAKEOVER mcap     — live $TAKEOVER market cap.
 *
 * Board data comes from `useLobbyBoards()`; the price/mcap from
 * `useTakeoverPrice()`. Each value falls back to a pulse skeleton while
 * its query loads and to `—` if the data is unavailable, so the strip
 * never renders a misleading zero.
 *
 * Client component (hooks); the parent `HeroSection` stays a server
 * component and mounts this for the live tail.
 */
export function HeroStatsRow() {
  // Pull the full board set (API caps at 250; ~174 live today) so the
  // aggregates below aren't truncated to the first page.
  const { all, isLoading: boardsLoading } = useLobbyBoards({ limit: 250 });
  const { priceUsd, marketCapUsd } = useTakeoverPrice();

  // Active = boards that paid out rewards in the last 7 days.
  const activeBoards = useMemo(
    () => all.filter((b) => parseUsdStr(b.rewards7d) > 0),
    [all],
  );

  // Total land value = Σ each active board's land value (sum of the
  // self-assessed price of its tax-paying seats), NOT the asset TVL.
  const { totalUsd: totalValueUsd, isLoading: landLoading } = useBoardsLand(
    activeBoards,
    true,
  );

  const totalRewardsUsd = useMemo(() => {
    let rewards = 0;
    // Lifetime rewards = yield + tax (numeric, set by the live grid
    // mapper). Summing the formatted `taxesUsd` string would miss all
    // yield and badly undercount the total.
    for (const board of all) rewards += board.rewardsTotalUsd ?? 0;
    return rewards;
  }, [all]);

  const activeCount = activeBoards.length;

  const stats: StatCell[] = [
    {
      label: 'Active boards',
      loading: boardsLoading,
      value: activeCount > 0 ? new Intl.NumberFormat('en-US').format(activeCount) : '—',
    },
    {
      label: 'Total land value',
      loading: boardsLoading || (landLoading && totalValueUsd === 0),
      value: totalValueUsd > 0 ? formatCompactUsd(totalValueUsd) : '—',
    },
    {
      label: 'Total rewards',
      loading: boardsLoading,
      value: totalRewardsUsd > 0 ? formatCompactUsd(totalRewardsUsd) : '—',
    },
    {
      // `marketCapUsd` is derived from the live price; it reads 0 both
      // while loading and if the price feed is unavailable, so show the
      // skeleton until a real (> 0) market cap lands.
      label: 'TAKEOVER mcap',
      loading: priceUsd === 0,
      value: marketCapUsd > 0 ? formatCompactUsd(marketCapUsd) : '—',
    },
  ];

  return (
    <dl className="grid grid-cols-2 gap-x-6 gap-y-6 pb-8 sm:grid-cols-4 sm:gap-x-8 lg:pb-10">
      {stats.map((stat) => (
        <div key={stat.label}>
          <dd className="font-mono text-3xl font-medium tracking-tight text-amber-50 lg:text-4xl">
            {stat.loading ? (
              <span className="inline-block h-[0.8em] w-[3.5ch] animate-pulse rounded bg-amber-500/15 align-middle" />
            ) : (
              stat.value
            )}
          </dd>
          <dt className="mt-2 font-mono text-[13px] uppercase tracking-[0.12em] text-zinc-500 lg:text-[15px] lg:tracking-[0.12em]">
            {stat.label}
          </dt>
        </div>
      ))}
    </dl>
  );
}

interface StatCell {
  label: string;
  value: string;
  loading: boolean;
}

/**
 * Parse a pre-formatted USD string (`"$612k"`, `"$1.2M"`, `"$38"`) back
 * to a number for aggregation. `MockBoard.tvl` / `.taxesUsd` are already
 * formatted at the `gridSummaryToMockBoard` mapper, so we re-parse the
 * display string the same way `BoardDirectory` / `TerritoriesSection`
 * do. The aggregate is necessarily approximate (each board's value is
 * pre-rounded), which is acceptable for a headline KPI. Returns `0` for
 * empty / unparseable input.
 */
function parseUsdStr(s: string | undefined | null): number {
  if (!s) return 0;
  const match = s.replace(/^\$/, '').match(/([\d,.]+)\s*([kmb]?)/i);
  if (!match) return 0;
  const value = parseFloat(match[1].replace(/,/g, ''));
  const suffix = match[2]?.toLowerCase();
  const mult = suffix === 'b' ? 1e9 : suffix === 'm' ? 1e6 : suffix === 'k' ? 1e3 : 1;
  return Number.isFinite(value) ? value * mult : 0;
}
